import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgArrow, SvgEye, SvgGoogle,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";

/**
 * /sign-in — email-code-first, password optional.
 *
 * Code path: requestSignIn(email) → /verify (which posts to /api/auth/verify).
 * Password path: signInWithPassword(email, password) → /home.
 */
export const SignIn = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { requestSignIn, signInWithPassword } = useAuth();
  const [pwMode, setPwMode] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    if (pwMode) {
      const err = await signInWithPassword(email, password, rememberMe);
      setSubmitting(false);
      if (err) return setError(err);
      navigate(params.get("next") ?? "/home", { replace: true });
      return;
    }
    const result = await requestSignIn(email);
    setSubmitting(false);
    if (!result.ok) return setError(result.message);
    sessionStorage.setItem("wo.auth.email", email.trim());
    sessionStorage.setItem("wo.auth.rememberMe", rememberMe ? "1" : "");
    if (result.devOtp) sessionStorage.setItem("wo.auth.devOtp", result.devOtp);
    else sessionStorage.removeItem("wo.auth.devOtp");
    navigate("/verify");
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "Welcome back",
        quote: <>You came back. <em>That alone</em> is a kind of progress.</>,
        cite: "— Today's reflection",
        features: [
          { icon: FeatIcon.lock, label: "Private by default" },
          { icon: FeatIcon.flame, label: "3 minutes a day" },
          { icon: FeatIcon.leaf, label: "No therapy, just clarity" },
        ],
      }}
      topRight={<Link to="/sign-up" className="btn-ghost">Create account</Link>}
      footer={<>New to Wise Owl Talks? <Link to="/sign-up">Create your private space →</Link></>}
    >
      <div className="eyebrow">Sign in</div>
      <h1 className="wo-title" style={{ marginTop: 6 }}>Pause for a moment.</h1>
      <p className="wo-sub">
        Welcome back. Sign in with your email — we&apos;ll send a 6-digit code, or you can use a password if you&apos;ve set one.
      </p>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label className="field-label" htmlFor="si-email">Email</label>
          <input
            id="si-email" className="field-input" type="email"
            placeholder="you@email.com" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)} required
          />
        </div>

        {pwMode && (
          <div className="field">
            <label className="field-label" htmlFor="si-pw">
              <span>Password</span>
              <span className="hint"><Link to="/forgot">Forgot?</Link></span>
            </label>
            <div className="field-pw-wrap">
              <input
                id="si-pw" className="field-input"
                type={showPw ? "text" : "password"}
                placeholder="••••••••" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button" className="pw-toggle"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
              >{SvgEye}</button>
            </div>
          </div>
        )}

        <label style={{
          display: "flex", alignItems: "center", gap: 8, margin: "10px 0 0",
          fontSize: 13, color: "rgb(var(--wo-ink-soft))", cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "rgb(var(--wo-teal))" }}
          />
          Keep me signed in on this device
        </label>

        {error && (
          <div className="field-help" style={{ color: "rgb(var(--wo-danger))", margin: "6px 0 0" }}>{error}</div>
        )}
        <button type="submit" className="btn-primary" style={{ marginTop: 8 }} disabled={submitting}>
          <span>{submitting ? "…" : pwMode ? "Sign in" : "Send me a code"}</span>
          {SvgArrow}
        </button>

        <button
          type="button"
          className="btn-ghost"
          style={{ width: "100%", marginTop: 14, height: 44 }}
          onClick={() => setPwMode((m) => !m)}
        >
          {pwMode ? "Use a code instead" : "Use a password instead"}
        </button>
      </form>

      <div className="or">or</div>

      <button type="button" className="btn-secondary">
        {SvgGoogle}
        Continue with Google
      </button>

      <p className="reflect">&ldquo;You don&apos;t need to solve everything today.&rdquo;</p>
    </AuthShell>
  );
};
